import { useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  Link,
  CircularProgress,
  Chip,
} from "@mui/material";
import Logo from "../assets/ZakazujMeLogo4.png";
const initialForm = {
  firstName: "",
  lastName: "",
  businessName: "",
  email: "",
  phone: "",
  message: "",
};

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
const isValidPhone = (value) => {
  const digits = value.replace(/\D/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

export function DemoThankYou() {
  return (
    <Grid
      container
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 800px at 20% 20%, #1e2a78 0%, #0b0f2b 55%, #0a0d20 100%)",
      }}
    >
      <Grid item xs={12}>
        <Box
          sx={{
            maxWidth: 780,
            mx: "auto",
            px: { xs: 2.5, sm: 4 },
            py: { xs: 8, sm: 10 },
            color: "white",
            textAlign: "center",
          }}
        >
          <Box
            component="img"
            src={Logo}
            alt="Zakazuj"
            sx={{ height: { xs: 56, sm: 72 }, mb: 2, opacity: 0.9 }}
          />
          <Typography variant="h3" sx={{ fontWeight: 900, mb: 2 }}>
            Hvala na zahtevu!
          </Typography>
          <Typography sx={{ opacity: 0.85, mb: 4 }}>
            Vaš zahtev za demo i saradnju je uspešno poslat. Javićemo se uskoro
            sa terminom prezentacije i narednim koracima.
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={2}
            justifyContent="center"
          >
            <Button
              component={RouterLink}
              to="/demo-request"
              variant="outlined"
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 999,
                fontWeight: 700,
                color: "white",
                borderColor: "rgba(255,255,255,0.35)",
              }}
            >
              Nazad na formu
            </Button>
            <Button
              component="a"
              href="https://zakazuj.com/demo"
              variant="contained"
              sx={{
                px: 3,
                py: 1.2,
                borderRadius: 999,
                fontWeight: 700,
                background: "linear-gradient(90deg, #3B82F6 0%, #06B6D4 100%)",
              }}
            >
              Otvori demo
            </Button>
          </Stack>
        </Box>
      </Grid>
    </Grid>
  );
}

export default function Demo() {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setFieldErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validate = () => {
    const nextErrors = {};

    if (!form.firstName.trim()) nextErrors.firstName = "Unesite ime.";
    if (!form.lastName.trim()) nextErrors.lastName = "Unesite prezime.";

    if (!form.email.trim()) {
      nextErrors.email = "Unesite email.";
    } else if (!isValidEmail(form.email.trim())) {
      nextErrors.email = "Unesite ispravan email.";
    }

    if (!form.phone.trim()) {
      nextErrors.phone = "Unesite telefon.";
    } else if (!isValidPhone(form.phone.trim())) {
      nextErrors.phone = "Unesite ispravan broj telefona.";
    }

    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSuccess("");
    setError("");

    if (!validate()) return;

    setLoading(true);
    try {
      const response = await fetch(
        "https://api.zakazuj.com/api/v1/contact/demo-request",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            firstName: form.firstName.trim(),
            lastName: form.lastName.trim(),
            businessName: form.businessName.trim(),
            email: form.email.trim(),
            phone: form.phone.trim(),
            message: form.message.trim(),
          }),
        },
      );

      if (!response.ok) {
        throw new Error("Došlo je do greške prilikom slanja zahteva.");
      }

      setSuccess("Hvala! Vaš zahtev je uspešno poslat. Javićemo se uskoro.");
      setForm(initialForm);
      navigate("/demo-request/thanks", { replace: true });
    } catch (err) {
      setError(err.message || "Došlo je do greške. Pokušajte ponovo.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Grid
      container
      sx={{
        minHeight: "100vh",
        background:
          "radial-gradient(1200px 800px at 20% 20%, #1e2a78 0%, #0b0f2b 55%, #0a0d20 100%)",
      }}
    >
      <Grid item xs={12}>
      <Box
        sx={{
          position: { xs: "static", md: "fixed" },
          top: { md: 18 },
          right: { md: 24 },
          display: "flex",
          flexWrap: "wrap",
          justifyContent: { xs: "center", md: "flex-end" },
          gap: { xs: 1, sm: 1.5, md: 2 },
          px: { xs: 2, sm: 3, md: 0 },
          py: { xs: 1, md: 0 },
          mt: { xs: 2, md: 0 },
          borderRadius: { xs: 2, md: 0 },
          background: {
            xs: "rgba(10, 13, 32, 0.6)",
            md: "transparent",
          },
          color: "rgba(255,255,255,0.95)",
          zIndex: 1100,
        }}
      >
          <Link
            underline="none"
            color="inherit"
            component={RouterLink}
            to="/home"
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 0.8, md: 1 },
            borderRadius: 2,
            fontWeight: 700,
            fontSize: { xs: 14, sm: 16, md: 25 },
            transition: "background 0.2s, color 0.2s",
            "&:hover": {
              background: "rgba(59,130,246,0.15)",
              color: "#3B82F6",
              },
            }}
          >
            Početna
          </Link>

          <Link
            underline="none"
            color="inherit"
            component={RouterLink}
            to="/cooperation"
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 0.8, md: 1 },
            borderRadius: 2,
            fontWeight: 700,
            fontSize: { xs: 14, sm: 16, md: 25 },
            transition: "background 0.2s, color 0.2s",
            "&:hover": {
              background: "rgba(59,130,246,0.15)",
              color: "#3B82F6",
              },
            }}
          >
            Saradnja
          </Link>

          <Link
            underline="none"
            color="inherit"
            component={RouterLink}
            to="/about"
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 0.8, md: 1 },
            borderRadius: 2,
            fontWeight: 700,
            fontSize: { xs: 14, sm: 16, md: 25 },
            transition: "background 0.2s, color 0.2s",
            "&:hover": {
              background: "rgba(59,130,246,0.15)",
              color: "#3B82F6",
              },
            }}
          >
            O nama
          </Link>

          <Link
            underline="none"
            color="inherit"
            component={RouterLink}
            to="/privacy-policy"
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 0.8, md: 1 },
            borderRadius: 2,
            fontWeight: 700,
            fontSize: { xs: 14, sm: 16, md: 25 },
            transition: "background 0.2s, color 0.2s",
            "&:hover": {
              background: "rgba(59,130,246,0.15)",
              color: "#3B82F6",
              },
            }}
          >
            Politika privatnosti
          </Link>

          <Link
            underline="none"
            color="inherit"
            component={RouterLink}
            to="/login"
            sx={{
              px: 2,
              py: 1,
              borderRadius: 2,
              fontWeight: 700,
              fontSize: 25,
              transition: "background 0.2s, color 0.2s",
              "&:hover": {
                background: "rgba(59,130,246,0.15)",
                color: "#3B82F6",
              },
            }}
          >
            Prijavi se
          </Link>

          <Link
            hidden
            underline="none"
            color="inherit"
            component={RouterLink}
            to="/userregistration"
          sx={{
            px: { xs: 1.5, sm: 2 },
            py: { xs: 0.5, sm: 0.8, md: 1 },
            borderRadius: 2,
            fontWeight: 700,
            fontSize: { xs: 14, sm: 16, md: 25 },
            background: "linear-gradient(90deg, #3B82F6 0%, #06B6D4 100%)",
            color: "white",
            boxShadow: "0 2px 8px rgba(59,130,246,0.25)",
              transition: "background 0.2s, color 0.2s",
              "&:hover": {
                background: "linear-gradient(90deg, #1D4ED8 0%, #06B6D4 100%)",
                color: "#fff",
              },
            }}
          >
            Registruj se
          </Link>
        </Box>

        <Box
          sx={{
            maxWidth: 1100,
            mx: "auto",
            px: { xs: 2.5, sm: 4 },
            py: { xs: 6, sm: 8 },
            color: "white",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              mb: 3,
            }}
          >
            <Box
              component="img"
              src={Logo}
              alt="Zakazuj"
              sx={{
                height: { xs: 64, sm: 80 },
                filter: "drop-shadow(0 0 16px rgba(124,58,237,0.45))",
              }}
            />
            <Box>
              <Typography variant="h3" sx={{ fontWeight: 900, mb: 0.5 }}>
                Da li je Zakazuj pravi za vas?
              </Typography>
              <Typography sx={{ opacity: 0.85 }}>
                Prvo isprobajte demo, a zatim nam pošaljite par informacija o
                vašoj radnji — mi ćemo vas kontaktirati i sve podesiti za vas.
              </Typography>
            </Box>
          </Box>

          <Grid container spacing={3} alignItems="stretch">
            <Grid item xs={12} md={5}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 3 },
                  height: "100%",
                  borderRadius: 3,
                  background:
                    "linear-gradient(140deg, rgba(59,130,246,0.2) 0%, rgba(6,182,212,0.12) 100%)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Stack spacing={2}>
                  <Chip
                    label="Live demo"
                    sx={{
                      bgcolor: "rgba(59,130,246,0.25)",
                      color: "white",
                      fontWeight: 700,
                      width: "fit-content",
                    }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Isprobajte Zakazuj odmah
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Demo okruženje omogućava da vidite kako izgleda rad sa
                    terminima, zaposlenima, resursima i klijentima — bez
                    registracije i bez obaveza.
                  </Typography>
                  <Stack spacing={1}>
                    {[
                      "Upravljanje terminima i rasporedom",
                      "Uvid u klijente i istoriju",
                      "Podešavanja radnje i neradni dani",
                      "Brza izmena i otkazivanje termina",
                    ].map((item) => (
                      <Typography key={item} variant="body2">
                        • {item}
                      </Typography>
                    ))}
                  </Stack>
                  <Button
                    component="a"
                    href="https://zakazuj.com/demo"
                    variant="contained"
                    sx={{
                      mt: 1,
                      px: 3,
                      py: 1.2,
                      borderRadius: 999,
                      fontWeight: 700,
                      background:
                        "linear-gradient(90deg, #3B82F6 0%, #06B6D4 100%)",
                    }}
                  >
                    Otvori demo
                  </Button>
                </Stack>
              </Paper>
            </Grid>

            <Grid item xs={12} md={7}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2.5, sm: 4 },
                  height: "100%",
                  borderRadius: 3,
                  background:
                    "linear-gradient(180deg, rgba(255,255,255,0.08) 0%, rgba(255,255,255,0.03) 100%)",
                  border: "1px solid rgba(255,255,255,0.1)",
                  backdropFilter: "blur(8px)",
                }}
              >
                <Stack spacing={2.5} component="form" onSubmit={handleSubmit}>
                  <Typography variant="h5" sx={{ fontWeight: 800 }}>
                    Kontakt za saradnju
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.85 }}>
                    Pošaljite nam osnovne informacije i predlog termina za demo.
                  </Typography>

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      name="firstName"
                      label="Ime *"
                      value={form.firstName}
                      onChange={handleChange}
                      fullWidth
                      disabled={loading}
                      error={Boolean(fieldErrors.firstName)}
                      helperText={fieldErrors.firstName}
                      variant="outlined"
                      InputLabelProps={{
                        style: { color: "rgba(255,255,255,0.7)" },
                      }}
                      InputProps={{ style: { color: "white" } }}
                    />
                    <TextField
                      name="lastName"
                      label="Prezime *"
                      value={form.lastName}
                      onChange={handleChange}
                      fullWidth
                      disabled={loading}
                      error={Boolean(fieldErrors.lastName)}
                      helperText={fieldErrors.lastName}
                      variant="outlined"
                      InputLabelProps={{
                        style: { color: "rgba(255,255,255,0.7)" },
                      }}
                      InputProps={{ style: { color: "white" } }}
                    />
                  </Stack>

                  <TextField
                    name="businessName"
                    label="Naziv radnje / firme"
                    value={form.businessName}
                    onChange={handleChange}
                    fullWidth
                    disabled={loading}
                    variant="outlined"
                    InputLabelProps={{
                      style: { color: "rgba(255,255,255,0.7)" },
                    }}
                    InputProps={{ style: { color: "white" } }}
                  />

                  <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                    <TextField
                      name="email"
                      label="Email *"
                      value={form.email}
                      onChange={handleChange}
                      type="email"
                      fullWidth
                      disabled={loading}
                      error={Boolean(fieldErrors.email)}
                      helperText={fieldErrors.email}
                      variant="outlined"
                      InputLabelProps={{
                        style: { color: "rgba(255,255,255,0.7)" },
                      }}
                      InputProps={{ style: { color: "white" } }}
                    />
                    <TextField
                      name="phone"
                      label="Telefon *"
                      value={form.phone}
                      onChange={handleChange}
                      fullWidth
                      disabled={loading}
                      error={Boolean(fieldErrors.phone)}
                      helperText={fieldErrors.phone}
                      variant="outlined"
                      InputLabelProps={{
                        style: { color: "rgba(255,255,255,0.7)" },
                      }}
                      InputProps={{ style: { color: "white" } }}
                    />
                  </Stack>

                  <TextField
                    name="message"
                    label="Poruka"
                    value={form.message}
                    onChange={handleChange}
                    fullWidth
                    multiline
                    minRows={4}
                    disabled={loading}
                    variant="outlined"
                    InputLabelProps={{
                      style: { color: "rgba(255,255,255,0.7)" },
                    }}
                    InputProps={{ style: { color: "white" } }}
                  />

                  {success && <Alert severity="success">{success}</Alert>}
                  {error && <Alert severity="error">{error}</Alert>}

                  <Stack
                    direction={{ xs: "column", sm: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", sm: "center" }}
                    justifyContent="space-between"
                  >
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={loading}
                      startIcon={
                        loading ? (
                          <CircularProgress size={18} sx={{ color: "white" }} />
                        ) : null
                      }
                      sx={{
                        px: 3,
                        py: 1.2,
                        borderRadius: 999,
                        fontWeight: 700,
                        background:
                          "linear-gradient(90deg, #3B82F6 0%, #06B6D4 100%)",
                      }}
                    >
                      {loading ? "Slanje..." : "Pošalji zahtev"}
                    </Button>
                  </Stack>
                  <Typography variant="caption" sx={{ opacity: 0.7 }}>
                    Slanjem zahteva pristajete da vas kontaktiramo u vezi
                    saradnje.
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Grid>
    </Grid>
  );
}
